import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, User, LayoutDashboard, Home, MessageCircle, Sun, Moon } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "../ui/sheet";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { usePublicTheme } from "../../context/PublicThemeContext";

const NAV = [
  { to: "/riders", label: "Riders" },
  { to: "/drivers", label: "Drivers" },
  { to: "/certification", label: "Test & Certification" },
  { to: "/resources", label: "Resources" },
  { to: "/events", label: "Events" },
  { to: "/news", label: "News" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, isAdmin, logout } = useAuth();
  const { light, toggle } = usePublicTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let active = true;
    const load = () => Promise.all([
      api.get("/chat/unread").catch(() => ({ data: {} })),
      api.get("/chat/members").catch(() => ({ data: [] })),
    ]).then(([g, m]) => {
      if (!active) return;
      const gt = Object.values(g.data || {}).reduce((a, b) => a + b, 0);
      const dt = (m.data || []).reduce((a, x) => a + (x.unread || 0), 0);
      setUnread(gt + dt);
    });
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0F2744]/95 border-b border-white/10" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 min-w-0" data-testid="nav-logo">
            <img src="/rospa-logo.png" alt="Rospa Advanced Drivers and Riders" className="h-14 w-14 sm:h-20 sm:w-20 object-contain shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="text-white font-heading font-extrabold text-xs sm:text-sm truncate max-w-[150px] sm:max-w-none">Rospa Advanced Drivers and Riders</div>
              <div className="text-amber-400 text-[10px] tracking-[0.2em] font-semibold">WEST MIDLANDS</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "text-amber-400" : "text-slate-200 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Button onClick={toggle} variant="ghost" size="icon" className="text-white hover:bg-white/10" title={light ? "Switch to dark mode" : "Switch to light mode"} data-testid="theme-toggle-btn">
              {light ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button asChild variant="ghost" size="icon" className="text-white hover:bg-white/10" title="Home">
              <Link to="/" data-testid="nav-home-btn"><Home className="h-5 w-5" /></Link>
            </Button>
            {user ? (
              <>
                <Button asChild variant="ghost" size="icon" className="relative text-white hover:bg-white/10" title="Messages">
                  <Link to="/member/community" data-testid="nav-messages-btn">
                    <MessageCircle className="h-5 w-5" />
                    {unread > 0 && <span data-testid="nav-messages-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[#0F2744] text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
                  </Link>
                </Button>
                <Button asChild variant="ghost" className="text-white hover:bg-white/10">
                  <Link to={isAdmin ? "/admin" : "/member"} data-testid="nav-dashboard-btn">
                    {isAdmin ? <LayoutDashboard className="h-4 w-4 mr-1.5" /> : <User className="h-4 w-4 mr-1.5" />}
                    {isAdmin ? "Admin" : "My Area"}
                  </Link>
                </Button>
                <Button onClick={handleLogout} variant="ghost" className="text-slate-300 hover:bg-white/10" data-testid="nav-logout-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button asChild variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white/10 hover:text-white" data-testid="nav-members-btn">
                <Link to="/login">Members</Link>
              </Button>
            )}
            <Button asChild className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold">
              <Link to="/join" data-testid="nav-join-btn">Join the Club</Link>
            </Button>
            <Button asChild variant="ghost" className="text-amber-300 hover:text-amber-200 hover:bg-white/10 font-semibold">
              <Link to="/assessment" data-testid="nav-assessment-btn">Free Assessment</Link>
            </Button>
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center gap-1.5">
            <Button onClick={toggle} size="icon" variant="ghost" className="text-white hover:bg-white/10" title={light ? "Dark mode" : "Light mode"} data-testid="theme-toggle-btn-mobile">
              {light ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            {user && (
              <Button asChild size="icon" variant="ghost" className="relative text-white hover:bg-white/10">
                <Link to="/member/community" data-testid="nav-messages-btn-mobile"><MessageCircle className="h-5 w-5" />
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[#0F2744] text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
                </Link>
              </Button>
            )}
            <Button asChild size="icon" variant="ghost" className="text-white hover:bg-white/10">
              <Link to="/" data-testid="nav-home-btn-mobile"><Home className="h-5 w-5" /></Link>
            </Button>
            <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold">
              <Link to="/join" data-testid="nav-join-btn-mobile">Join</Link>
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" className="text-white hover:bg-white/10" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] sm:w-80 bg-[#0F2744] border-l border-white/10 text-white overflow-y-auto">
                <div className="flex flex-col gap-1 mt-8">
                  {NAV.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className={({ isActive }) =>
                          `px-4 py-3 rounded-lg text-base font-medium ${
                            isActive ? "bg-white/10 text-amber-400" : "text-slate-200"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                  <div className="h-px bg-white/10 my-3" />
                  {user ? (
                    <>
                      <SheetClose asChild>
                        <Link to="/member/community" className="px-4 py-3 rounded-lg text-base font-medium text-slate-200 flex items-center gap-2" data-testid="mobile-messages-btn">
                          <MessageCircle className="h-4 w-4" /> Messages{unread > 0 ? ` (${unread > 9 ? "9+" : unread})` : ""}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to={isAdmin ? "/admin" : "/member"} className="px-4 py-3 rounded-lg text-base font-medium text-slate-200" data-testid="mobile-dashboard-btn">
                          {isAdmin ? "Admin Area" : "My Member Area"}
                        </Link>
                      </SheetClose>
                      <button onClick={handleLogout} className="text-left px-4 py-3 rounded-lg text-base font-medium text-slate-300" data-testid="mobile-logout-btn">
                        Sign out
                      </button>
                    </>
                  ) : (
                    <SheetClose asChild>
                      <Link to="/login" className="px-4 py-3 rounded-lg text-base font-medium text-slate-200" data-testid="mobile-login-btn">
                        Member Login
                      </Link>
                    </SheetClose>
                  )}
                  <SheetClose asChild>
                    <Link to="/join" className="mt-2 px-4 py-3 rounded-lg text-base font-bold text-center bg-amber-500 text-[#0F2744]" data-testid="mobile-join-btn">
                      Join the Club
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/assessment" className="px-4 py-3 rounded-lg text-base font-semibold text-center text-amber-300 border border-amber-400/40" data-testid="mobile-assessment-btn">
                      Free Assessment
                    </Link>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
